"use client"

import { Link, useLocation } from "react-router-dom"
import { useEffect } from "react"
import { useTheme } from "../contexts/ThemeContext"

function Footer() {
  const currentYear = new Date().getFullYear()
  const location = useLocation()
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
  }, [location.pathname])

  return (
    <footer className={theme === "dark" ? "bg-black text-white border-t border-gray-800" : "bg-gray-900 text-white"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              {/* Brand Logo SVG (Instagram-like gradient, AI/brain icon) */}
              <img
                src={process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/brand-logo.svg' : '/brand-logo.svg'}
                alt="Inflatein AI Studio Logo"
                className={theme === "dark" ? "w-9 h-9 rounded-lg shadow-lg bg-gray-800 p-0.5 border border-gray-700" : "w-9 h-9 rounded-lg shadow-lg bg-gradient-to-r from-indigo-600 via-pink-500 to-purple-600 p-0.5"}
                draggable="false"
              />
              <span className={theme === "dark" ? "ml-2 text-xl font-bold bg-gradient-to-r from-gray-100 via-gray-400 to-gray-700 bg-clip-text text-transparent drop-shadow" : "ml-2 text-xl font-bold bg-gradient-to-r from-indigo-400 via-pink-400 to-purple-500 bg-clip-text text-transparent drop-shadow"}>Inflatein AI Studio</span>
            </div>
            <p className={theme === "dark" ? "text-gray-300 text-sm mb-4" : "text-gray-400 text-sm mb-4"}>
              Transform your ideas into stunning visuals with Inflatein AI Studio. 
              Create, customize, and download high-quality images in seconds.
            </p>
            <div className="flex space-x-4">
              <a href="#" className={theme === "dark" ? "text-gray-400 hover:text-white transition-colors" : "text-gray-400 hover:text-white transition-colors"}>
                <span className="sr-only">Twitter</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="#" className={theme === "dark" ? "text-gray-400 hover:text-white transition-colors" : "text-gray-400 hover:text-white transition-colors"}>
                <span className="sr-only">GitHub</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className={theme === "dark" ? "text-gray-400 hover:text-white transition-colors" : "text-gray-400 hover:text-white transition-colors"}>
                <span className="sr-only">LinkedIn</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 tracking-wider uppercase mb-4">Product</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Image Generator
                </Link>
              </li>
              <li>
                <Link to="/subscription" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Pricing Plans
                </Link>
              </li>
              <li>
                <Link to="/api" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  API Access
                </Link>
              </li>
              <li>
                <Link to="/enterprise" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Enterprise
                </Link>
              </li>
              <li>
                <Link to="/changelog" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Changelog
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 tracking-wider uppercase mb-4">Support</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/help" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Help Center
                </Link>
              </li>
              <li>
                <Link to="/docs" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Documentation
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/status" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Status Page
                </Link>
              </li>
              <li>
                <Link to="/community" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Community
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/report" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Report an Issue
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 tracking-wider uppercase mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/press" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Press
                </Link>
              </li>
              <li>
                <Link to="/partners" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Partners
                </Link>
              </li>
              <li>
                <Link to="/legal" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Legal & Policies
                </Link>
              </li>
              <li>
                <Link to="/security" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Security
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-gray-400 hover:text-white transition-colors text-sm" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Our Team
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
              <p className="text-gray-400 text-sm">
                © {currentYear} Inflatein AI Studio. All rights reserved.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Terms of Service
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Cookie Policy
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <span className="text-gray-400 text-sm">Made with ❤️ in India</span>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 text-xs font-medium">Live</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer